import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ChevronRight, Target, Share2 } from 'lucide-react';
import { RARITY_UI } from '../lib/rarity';
import RarityBadge from './RarityBadge';
import DiscoveryReveal from './DiscoveryReveal';
import ShareCardModal from './ShareCardModal';
import Confetti from './Confetti';

/**
 * Wraps DiscoveryReveal with two extra beats:
 *   1. First-ever discovery gets a full "Explorer Begins" cinematic.
 *   2. Every new species (rarity uncommon+) offers a share card afterwards.
 *
 * The rarity gate makes the ask feel earned rather than spammy.
 */
export default function FirstDiscoveryFlow({ result, onClose, currentUser }) {
  const isFirst = !!result?.is_first_ever;
  const isNewSpecies = !!result?.new_species;
  const rarity = result?.species?.rarity || 'common';
  const rarityScore = ['common','uncommon','rare','epic','legendary','mythic'].indexOf(rarity);
  const worthySpecies = isNewSpecies && rarityScore >= 1; // uncommon or better
  const isWorldFirst = !!result?.world_first;
  const isRegionalFirst = !!result?.regional_first;
  const shouldOfferShare = isFirst || isWorldFirst || isRegionalFirst || worthySpecies
    || rarity === 'legendary' || rarity === 'mythic';

  const [stage, setStage] = useState(isFirst ? 'welcome' : 'reveal');

  useEffect(() => {
    if (stage !== 'welcome') return;
    const t = setTimeout(() => setStage('reveal'), 4200);
    return () => clearTimeout(t);
  }, [stage]);

  if (!result) return null;

  if (stage === 'welcome') {
    return <FirstDiscoveryWelcome result={result} onSkip={() => setStage('reveal')} />;
  }

  const advance = () => {
    if (shouldOfferShare) {
      setStage('share');
    } else {
      onClose();
    }
  };

  const moment = buildMoment({ result, currentUser, isFirst, isWorldFirst, isRegionalFirst });

  return (
    <>
      <DiscoveryReveal result={result} onClose={advance} />
      {stage === 'share' && (
        <ShareCardModal moment={moment} onClose={() => setStage('next') } />
      )}
      {stage === 'next' && isFirst && (
        <NextMission onClose={onClose} />
      )}
      {stage === 'next' && !isFirst && onClose()}
    </>
  );
}

function buildMoment({ result, currentUser, isFirst, isWorldFirst, isRegionalFirst }) {
  const species = result.species || {};
  const type = isFirst ? 'first_discovery'
    : isWorldFirst ? 'world_first'
    : isRegionalFirst ? 'regional_first'
    : species.rarity === 'mythic' ? 'mythic'
    : species.rarity === 'legendary' ? 'legendary'
    : 'new_species';
  const title = isFirst ? 'Explorer Begins'
    : isWorldFirst ? `World-First: ${species.common_name}`
    : isRegionalFirst ? `Regional-First: ${species.common_name}`
    : species.common_name || 'New Species';
  const shareUrl = `${window.location.origin}/d/${result.observation?.observation_id || ''}`;
  const location = result.area?.name || result.observation?.area || null;
  return {
    type, title,
    subtitle: species.scientific_name,
    species,
    score: result.score?.total,
    score_label: 'DISCOVERY SCORE',
    player: currentUser ? { name: currentUser.name, picture: currentUser.picture } : null,
    location,
    date: result.observation?.created_at,
    share_url: shareUrl,
  };
}

// ============================================================================
//  "Explorer Begins" welcome cinematic (first discovery only)
// ============================================================================
function FirstDiscoveryWelcome({ result, onSkip }) {
  const species = result.species;
  const ui = RARITY_UI[species?.rarity] || RARITY_UI.legendary;
  return (
    <AnimatePresence>
      <motion.div
        data-testid="first-discovery-welcome"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 z-[110] flex items-center justify-center overflow-hidden"
        style={{ background: 'radial-gradient(ellipse at center top, rgba(233,196,106,0.20), transparent 55%), rgba(4,10,8,0.98)' }}
      >
        <GoldRaysGrand />
        <Confetti seed={result.observation?.observation_id || 'welcome'} />
        <motion.button
          data-testid="first-welcome-skip" onClick={onSkip}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}
          className="absolute top-6 right-6 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground z-10"
        >Skip ›</motion.button>
        <div className="relative max-w-md w-full text-center px-8 space-y-6">
          <motion.div
            initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.4, type: 'spring', stiffness: 140, damping: 14 }}
            className="mx-auto w-40 h-40 rounded-full flex items-center justify-center relative"
            style={{ background: 'radial-gradient(circle, rgba(233,196,106,0.35), transparent 65%)' }}
          >
            <motion.div className="absolute inset-0 rounded-full border-4"
              style={{ borderColor: 'rgba(233,196,106,0.35)' }}
              animate={{ scale: [1, 1.15, 1], opacity: [1, 0, 1] }}
              transition={{ duration: 2.4, repeat: Infinity }} />
            <motion.div className="w-28 h-28 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #E9C46A, #B08B58)',
                boxShadow: '0 0 40px rgba(233,196,106,0.6), inset 0 0 20px rgba(255,255,255,0.15)',
              }}
              animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              <Sparkles className="w-14 h-14 text-black" strokeWidth={2} />
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}>
            <p className="text-[10px] uppercase tracking-[0.4em] text-amber-300">Achievement Unlocked</p>
            <h2 className="font-serif text-4xl mt-2 leading-tight">Explorer Begins</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-xs mx-auto leading-relaxed">
              You've officially become a <span className="text-amber-300">WildQuest Explorer</span>. A permanent badge now marks your profile.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.9, duration: 0.6 }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${ui.cardClass}`}
            style={ui.cardStyle}
          >
            <RarityBadge rarity={species?.rarity || 'common'} size="sm" showParticles={false} />
            <span className="font-serif text-sm">Your first discovery: <span style={{ color: ui.color }}>{species?.common_name}</span></span>
          </motion.div>
          <motion.button data-testid="first-welcome-continue"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.6 }}
            onClick={onSkip}
            className="mt-6 px-6 py-3 rounded-full bg-amber-400 text-black text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-2">
            See the reveal <ChevronRight className="w-3 h-3" />
          </motion.button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function GoldRaysGrand() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {Array.from({ length: 14 }).map((_, i) => (
        <motion.div key={i} className="absolute left-1/2 top-1/2 origin-top"
          style={{
            width: 3, height: '75vh',
            background: 'linear-gradient(180deg, rgba(233,196,106,0.55), transparent 65%)',
            transform: `rotate(${(i * 360) / 14}deg)`, filter: 'blur(1.2px)',
          }}
          animate={{ opacity: [0.3, 0.9, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, delay: i * 0.15 }} />
      ))}
    </div>
  );
}

// Post-share "Next Mission" hint (shown only after first-ever share)
function NextMission({ onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        data-testid="first-next-mission-modal"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[106] flex items-center justify-center px-6"
        style={{ background: 'rgba(4,10,8,0.97)' }}
      >
        <div className="max-w-md w-full text-center space-y-4">
          <div className="rounded-3xl bg-gradient-to-br from-emerald-900/30 via-primary/10 to-transparent border border-emerald-400/30 p-6">
            <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-[0.25em] text-emerald-300">
              <Target className="w-3.5 h-3.5" /> Next Mission
            </div>
            <p className="font-serif text-2xl mt-2">Find your second species</p>
            <p className="text-xs text-muted-foreground mt-2">Every explorer's journey deepens with each new find.</p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <MiniReward label="XP"    value="+250"  accent="#E9C46A" />
              <MiniReward label="Coins" value="+50"   accent="#FFD166" />
              <MiniReward label="Card"  value="Rare?" accent="#8B5CF6" />
            </div>
          </div>
          <button
            data-testid="first-mission-continue"
            onClick={onClose}
            className="w-full py-3.5 rounded-full bg-accent text-black font-semibold text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5" /> Keep exploring
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function MiniReward({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-black/40 border border-white/5 py-2">
      <p className="font-mono text-sm tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}
